/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isGameModeValue, isRelative } from "@cosmos/common-utils";

import type { SilentLoginOffer } from "./auth.fetch";

/*
  Internal to next-hooks on purpose: not re-exported from the auth barrel. The only supported entry
  point is `useSilentLogin`, which also enforces the signed-out check; a second caller of
  `trySilentLogin` could navigate a signed-in page.
*/

/**
 * `localStorage` key holding this origin's attempt record: `{ tried, at, count }` — the fingerprints
 * not to follow (most recent {@link MAX_REMEMBERED_FINGERPRINTS}), and the start and count of the
 * current attempt window. Per origin rather than per tab, so several open tabs do not each
 * redirect. Reset by {@link resetSilentLoginAttempts} once the user is signed in.
 */
const LAST_ATTEMPT_KEY = "cosmos:silent-login-attempt";

/**
 * How many fingerprints to remember. More than one, so two SSO cookie values that alternate cannot
 * each look new every time and slip past the once-per-fingerprint rule.
 */
const MAX_REMEMBERED_FINGERPRINTS = 5;

/**
 * At most {@link MAX_ATTEMPTS_PER_WINDOW} attempts per window, whatever the fingerprints say. The
 * fingerprint alone stops a stale cookie from being retried; this bounds the case it cannot see —
 * Account Portal rewriting its SSO cookies without a real sign-in, which would make every rewrite
 * look like a new offer.
 *
 * Two, not one. The common sequence is a stale SSO cookie left over from an earlier session (its
 * attempt fails on the first page view), then a real sign-in elsewhere minutes later. A one-attempt
 * budget made that real sign-in wait out the window — seen on gamedev, where it read as "does not
 * work".
 */
const ATTEMPT_WINDOW_MS = 10 * 60 * 1000;
const MAX_ATTEMPTS_PER_WINDOW = 2;

/**
 * Query params the auth worker appends when a sign-in failed (`AUTH_ERROR_PARAMS` in
 * `online-identity/cloudflare-auth-proxy`). A page carrying them has just come back from a failed
 * attempt; trying again at once would fail the same way.
 */
const AUTH_ERROR_PARAMS = ["auth_error_code", "auth_error_description"];

/** The pathname the user last typed into or changed something on, if any. */
let interactedPath: string | undefined;
let tracking = false;

const markInteracted = (event: Event): void => {
	// Only what the user did. A script that sets a control and dispatches `change` on load (a
	// consent banner, a tag) must not switch silent sign-in off for the whole path.
	if (!event.isTrusted) return;
	interactedPath = window.location.pathname;
};

/**
 * Start noting where the user types into or changes anything. A silent sign-in is a full-page
 * navigation, so it must never fire over unsaved input: a half-filled form, a half-read agreement.
 * Capture phase so a handler that stops propagation cannot hide it; `input` covers text fields and
 * contenteditable, `change` covers checkboxes, radios and selects.
 *
 * Input is scoped to the pathname it happened on, and there is deliberately no reset: a remount
 * (App Router remounts a segment when only the search params change) or a second hook mounting
 * late must not forget input made earlier on the same page. A client-side navigation to another
 * path leaves it behind naturally.
 *
 * Installed once per document and never removed: two passive listeners cost nothing.
 */
export const trackUserInteraction = (): void => {
	if (tracking || typeof document === "undefined") return;
	tracking = true;
	document.addEventListener("input", markInteracted, { capture: true, passive: true });
	document.addEventListener("change", markInteracted, { capture: true, passive: true });
};

/** `<input>` types a user types text into. */
const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "password", "number"]);

const isEditable = (element: Element | null): boolean =>
	element instanceof HTMLElement && (element.isContentEditable || element.matches("input, textarea, select"));

/**
 * A text field whose value differs from its server-rendered default: text typed before hydration
 * installed the listeners. Text entry only, on purpose. Checkboxes, radios and selects report
 * `change` once hydrated, and their DOM state is unreliable as a signal: a native `<select>` with no
 * `selected` attribute reads as changed on every page, and scripts such as the cookie-consent
 * banner set checkbox state programmatically. Either would silently switch the feature off.
 */
const isTypedInto = (control: Element): boolean => {
	if (control instanceof HTMLTextAreaElement) return control.value !== control.defaultValue;
	if (!(control instanceof HTMLInputElement)) return false;
	return TEXT_INPUT_TYPES.has(control.type) && control.value !== control.defaultValue;
};

/**
 * Whether leaving the page now could lose something: recorded input on this path, a focused field,
 * or a text field typed into before hydration.
 */
const hasUnsavedInput = (): boolean =>
	interactedPath === window.location.pathname ||
	isEditable(document.activeElement) ||
	Array.from(document.querySelectorAll("input, textarea")).some(isTypedInto);

const isGameMode = (search: string): boolean => new URLSearchParams(search).getAll("mode").some(isGameModeValue);

const hasAuthError = (search: string): boolean => {
	const params = new URLSearchParams(search);
	return AUTH_ERROR_PARAMS.some((param) => params.has(param));
};

/**
 * Start a fresh attempt record, so the next signed-out spell has a full budget — except for the
 * Account Portal SSO state of the session that is live now (`sessionFingerprint`), which is marked
 * as already tried.
 *
 * Call only on a session check that confirms the user is signed in. That covers sign-out too: the
 * user was signed in right before, and no attempts are made while signed in. Remembering the live
 * session's fingerprint is what makes sign-out stick: signing out here kills the Account Portal
 * session but can leave its SSO cookie on this domain, and following that cookie would bounce the
 * user through a sign-in that cannot succeed — or, if the Account Portal session somehow survived,
 * silently undo the sign-out. A real sign-in elsewhere writes a new value, which is still followed.
 *
 * Deliberately not reset when an attempt merely looked successful: if the round trip completes but
 * the session cookie never sticks, there is no signed-in response, the record stays, and the same
 * fingerprint is not retried in a loop.
 */
export const resetSilentLoginAttempts = (sessionFingerprint?: string): void => {
	try {
		if (sessionFingerprint) {
			window.localStorage.setItem(LAST_ATTEMPT_KEY, JSON.stringify({ tried: [sessionFingerprint] }));
		} else {
			window.localStorage.removeItem(LAST_ATTEMPT_KEY);
		}
	} catch {
		// Nothing recorded without storage, so nothing to reset.
	}
};

/** Whether this origin may attempt now, and if so, records the attempt. False when storage fails. */
const claimAttempt = (fingerprint: string): boolean => {
	try {
		const record = JSON.parse(window.localStorage.getItem(LAST_ATTEMPT_KEY) ?? "null") as {
			tried?: unknown;
			fingerprint?: unknown;
			at?: unknown;
			count?: unknown;
		} | null;
		// `fingerprint` is the single-entry shape written by earlier builds.
		const tried = Array.isArray(record?.tried)
			? record.tried.filter((entry): entry is string => typeof entry === "string")
			: typeof record?.fingerprint === "string"
				? [record.fingerprint]
				: [];
		if (tried.includes(fingerprint)) return false;

		const now = Date.now();
		// Forward time only: a clock set back must not keep an old window open.
		const elapsed = typeof record?.at === "number" ? now - record.at : Number.POSITIVE_INFINITY;
		const inWindow = elapsed >= 0 && elapsed < ATTEMPT_WINDOW_MS;
		const count = inWindow ? (typeof record?.count === "number" ? record.count : 1) : 0;
		if (count >= MAX_ATTEMPTS_PER_WINDOW) return false;

		window.localStorage.setItem(
			LAST_ATTEMPT_KEY,
			JSON.stringify({
				tried: [...tried, fingerprint].slice(-MAX_REMEMBERED_FINGERPRINTS),
				// `at` marks the start of the window, so a burst cannot keep extending it.
				at: inWindow ? record?.at : now,
				count: count + 1,
			}),
		);
		return true;
	} catch {
		return false;
	}
};

/**
 * Send this tab through the silent sign-in the server offered, returning here afterwards.
 *
 * Returns whether it navigated. It declines when:
 *  - it is not the top-level document: the auth worker's cookies are `SameSite=Lax`, so a framed
 *    sign-in cannot stick (the Hygraph preview loop, MW-13886);
 *  - the tab is hidden: nobody is looking, and a background navigation would be a surprise;
 *  - leaving could lose something (see {@link hasUnsavedInput});
 *  - the page is a game-mode webview, which has no way back from a sign-in redirect. The edge's
 *    own game-mode exemption keys off the document URL, and the session check that produced this
 *    offer was a `fetch` without the `mode` param, so it has to be re-checked here;
 *  - the page has just come back from a failed sign-in (auth error params);
 *  - the URL is not this origin's `/auth/login?prompt=none`;
 *  - this origin already tried the same fingerprint, or already made {@link MAX_ATTEMPTS_PER_WINDOW}
 *    attempts in the current window — or `localStorage` is unavailable, which leaves no way to know.
 *
 * `location.replace`, so the pre-redirect page does not become a Back-button entry.
 */
export const trySilentLogin = (offer: SilentLoginOffer): boolean => {
	if (typeof window === "undefined" || window.top !== window.self) return false;
	if (document.visibilityState !== "visible" || hasUnsavedInput()) return false;

	const { origin, pathname, search, hash } = window.location;
	if (isGameMode(search) || hasAuthError(search)) return false;

	let url: URL;
	try {
		url = new URL(offer.url, window.location.href);
	} catch {
		return false;
	}
	if (url.origin !== origin || url.pathname !== "/auth/login" || url.searchParams.get("prompt") !== "none") {
		return false;
	}

	if (!claimAttempt(offer.fingerprint)) return false;

	// Same open-redirect guard as `toReturnPath` in next-app-token. The worker also rejects a
	// returnPath that resolves off-origin, but it should never be handed `//evil.com` at all.
	const returnPath = `${pathname}${search}${hash}`;
	url.searchParams.set("returnPath", isRelative(returnPath) ? returnPath : "/");
	window.location.replace(url.toString());
	return true;
};
